/**
 * SAM-842: the companions of a pass as the guests step resolved them, one per extra adult: scanned (the
 * document's data wins) or typed (the name typed on the step wins over the generic "Guest N" placeholder).
 * The direct-create path of DocumentScanForm used to rebuild them from the placeholders and the scans only,
 * so a typed companion reached the API as "Guest 3".
 */

export interface ScannedDoc {
  name?: string;
  LastName?: string;
  number?: string | null;
  nationality?: string | null;
  DOB?: string | null;
  picture?: string | null;
}

export interface CompanionSlot {
  firstName: string;
  lastName: string;
}

export interface ResolvedCompanion {
  firstName: string;
  lastName: string;
  tel: string;
  photo_url: string | null;
  idnum: string | null;
  nationality: string | null;
  birthdate: string | null;
}

export interface ResolveCompanionsInput {
  companions: CompanionSlot[];
  /** Mode chosen per guest id (`firstName-lastName` of the slot); absent = scan when a document is loaded. */
  modes: Record<string, 'scan' | 'manual'>;
  /** The scanned document of a guest id, if any. */
  docOf: (guestId: string) => ScannedDoc | undefined;
  phones: Record<string, string>;
  /** Names typed on the step, by companion index. */
  manual: Array<Partial<CompanionSlot> | undefined>;
}

export const resolveCompanions = ({ companions, modes, docOf, phones, manual }: ResolveCompanionsInput): ResolvedCompanion[] =>
  companions.map((companion, index) => {
    const id = `${companion.firstName}-${companion.lastName}`;
    const doc = docOf(id);
    const mode = modes[id] || (doc ? 'scan' : 'manual');
    const tel = phones[id] || '';

    if (mode === 'scan') {
      return {
        firstName: doc?.name || companion.firstName,
        lastName: doc?.LastName || companion.lastName,
        tel,
        photo_url: doc?.picture || null,
        idnum: doc?.number || null,
        nationality: doc?.nationality || null,
        birthdate: doc?.DOB || null,
      };
    }
    const typed = manual[index];
    return {
      firstName: typed?.firstName || companion.firstName,
      lastName: typed?.lastName || companion.lastName,
      tel,
      photo_url: null,
      idnum: null,
      nationality: null,
      birthdate: null,
    };
  });

/** A resolved companion as `business_guests` takes it. */
export const toBusinessGuest = (c: ResolvedCompanion) => ({
  name: c.firstName,
  last_name: c.lastName,
  title_es: 'Acompañante',
  tel: c.tel,
  photo_url: c.photo_url,
  idnum: c.idnum,
  nationality: c.nationality,
  birthdate: c.birthdate,
});
